'use client'

import {
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import type { GalleryPhotosQuery } from '@/generated/graphql'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { PHOTO_HASH_PREFIX } from './constants'
import {
  getGalleryImageUrl,
  getGalleryPhotoDownloadUrl,
  getGalleryPhotoLabel,
} from './utils'

const MASONRY_ROW_HEIGHT = 4
const MASONRY_ROW_GAP = 8

const getMasonryRowSpan = (height: number) =>
  Math.ceil((height + MASONRY_ROW_GAP) / (MASONRY_ROW_HEIGHT + MASONRY_ROW_GAP))

const DEFAULT_MASONRY_ROW_SPAN = getMasonryRowSpan(200)
const PLACEHOLDER_MASONRY_ROW_SPAN = getMasonryRowSpan(192)
const MASONRY_GRID_STYLE: CSSProperties = {
  gridAutoRows: `${MASONRY_ROW_HEIGHT}px`,
  rowGap: `${MASONRY_ROW_GAP}px`,
}

type GalleryPhoto = GalleryPhotosQuery['galleryPhotos'][number]

const useMasonryPhotoGrid = (photos: GalleryPhoto[]) => {
  const photoImageRefs = useRef(new Map<number, HTMLImageElement>())
  const [photoRowSpans, setPhotoRowSpans] = useState<Record<number, number>>({})

  const updatePhotoRowSpan = useCallback(
    (photoId: number, element: HTMLElement | null) => {
      if (!element) {
        return
      }

      const height = element.getBoundingClientRect().height

      if (height <= 0) {
        return
      }

      const rowSpan = getMasonryRowSpan(height)

      setPhotoRowSpans(currentRowSpans => {
        if (currentRowSpans[photoId] === rowSpan) {
          return currentRowSpans
        }

        return { ...currentRowSpans, [photoId]: rowSpan }
      })
    },
    []
  )

  const registerPhotoImage = useCallback(
    (photoId: number, element: HTMLImageElement | null) => {
      if (element) {
        photoImageRefs.current.set(photoId, element)
      } else {
        photoImageRefs.current.delete(photoId)
      }
    },
    []
  )

  const getPhotoRowSpan = useCallback(
    (photo: GalleryPhoto) =>
      photo.imageUrl
        ? (photoRowSpans[photo.id] ?? DEFAULT_MASONRY_ROW_SPAN)
        : PLACEHOLDER_MASONRY_ROW_SPAN,
    [photoRowSpans]
  )

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(entries => {
      entries.forEach(entry => {
        const photoId = Number(
          (entry.target as HTMLElement).dataset.galleryPhotoId
        )

        if (!Number.isFinite(photoId)) {
          return
        }

        updatePhotoRowSpan(photoId, entry.target as HTMLElement)
      })
    })

    const imageElements = Array.from(photoImageRefs.current.values())

    imageElements.forEach(element => observer.observe(element))

    return () => observer.disconnect()
  }, [photos, updatePhotoRowSpan])

  return {
    getPhotoRowSpan,
    registerPhotoImage,
    updatePhotoRowSpan,
  }
}

interface GalleryPhotoButtonProps {
  index: number
  onOpen: (index: number) => void
  photo: GalleryPhoto
  registerImage: (photoId: number, element: HTMLImageElement | null) => void
  rowSpan: number
  updateRowSpan: (photoId: number, element: HTMLElement | null) => void
}

const GalleryPhotoButton = ({
  index,
  onOpen,
  photo,
  registerImage,
  rowSpan,
  updateRowSpan,
}: GalleryPhotoButtonProps) => (
  <div
    id={`${PHOTO_HASH_PREFIX}${photo.id}`}
    className="group relative block w-full self-start overflow-hidden rounded-lg bg-gray-900 text-left shadow-md transition-shadow duration-300 hover:shadow-2xl focus-within:ring-2 focus-within:ring-black/30"
    style={{ gridRowEnd: `span ${rowSpan}` }}
  >
    <button
      type="button"
      className="block w-full focus:outline-none"
      onClick={() => onOpen(index)}
      aria-label={`Open ${getGalleryPhotoLabel(photo)}`}
    >
      {photo.imageUrl ? (
        <img
          ref={element => registerImage(photo.id, element)}
          src={getGalleryImageUrl(photo.imageUrl, 800)}
          alt={getGalleryPhotoLabel(photo)}
          data-gallery-photo-id={photo.id}
          className="block h-auto w-full transition-transform duration-500 group-hover:scale-[1.03]"
          loading={index < 6 ? 'eager' : 'lazy'}
          onLoad={event => updateRowSpan(photo.id, event.currentTarget)}
        />
      ) : (
        <div className="flex min-h-48 w-full items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
          <span className="text-gray-500 text-sm">No image</span>
        </div>
      )}
    </button>
    <div className="pointer-events-none absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/20" />
    <div className="pointer-events-none absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-within:opacity-100">
      {photo.title && (
        <p className="text-white text-sm font-medium truncate">{photo.title}</p>
      )}
    </div>
    {photo.imageUrl && (
      <a
        href={getGalleryPhotoDownloadUrl(photo)}
        className="absolute right-3 top-3 inline-flex rounded-full bg-black/65 p-2 text-white opacity-0 shadow-md backdrop-blur-sm transition-all duration-200 hover:bg-black/80 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/80 group-hover:opacity-100 group-focus-within:opacity-100"
        aria-label={`Download ${getGalleryPhotoLabel(photo)}`}
        title="Download photo"
      >
        <ArrowDownTrayIcon className="h-5 w-5" />
      </a>
    )}
  </div>
)

interface GalleryPhotoGridProps {
  onPhotoOpen: (index: number) => void
  photos: GalleryPhoto[]
}

export const GalleryPhotoGrid = ({
  onPhotoOpen,
  photos,
}: GalleryPhotoGridProps) => {
  const { getPhotoRowSpan, registerPhotoImage, updatePhotoRowSpan } =
    useMasonryPhotoGrid(photos)

  return (
    <div
      className="grid grid-cols-1 gap-x-4 sm:grid-cols-2 lg:grid-cols-3 mb-8"
      style={MASONRY_GRID_STYLE}
    >
      {photos.map((photo, index) => (
        <GalleryPhotoButton
          key={photo.id}
          index={index}
          onOpen={onPhotoOpen}
          photo={photo}
          registerImage={registerPhotoImage}
          rowSpan={getPhotoRowSpan(photo)}
          updateRowSpan={updatePhotoRowSpan}
        />
      ))}
    </div>
  )
}
